import {
  Avatar,
  Badge,
  Box,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { IDisabledVeteran } from 'API/DisabledVeteran/disabledVeteran';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { PurpleCheckMark } from 'Images';

interface IDisabledVeteranPaper {
  disabledVeteran: IDisabledVeteran;
}

export const DisabledVeteranPaper = ({
  disabledVeteran,
}: IDisabledVeteranPaper) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.DisabledVeteran);
  const isDesktop = useIsDesktop();

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: '#fff',
        boxShadow: '0px 4px 28px rgba(45, 112, 217, 0.15)',
        borderRadius: '16px',
        padding: '32px 32px 40px',
      }}
    >
      <Stack gap={'32px'}>
        <Stack gap={'20px'}>
          <Stack gap={'8px'}>
            <Stack direction={'row'} gap={'8px'} alignItems={'flex-end'}>
              <Typography variant={'body7'} tabIndex={0}>
                {dictionary.he.name}
              </Typography>
              <Divider
                orientation={'vertical'}
                flexItem
                aria-hidden={'true'}
                sx={{ borderColor: 'primary.shade_100' }}
              />
              <Typography variant={'body7'} tabIndex={0}>
                {dictionary.en.name}
              </Typography>
            </Stack>
            <Stack
              direction={isDesktop ? 'row' : 'column'}
              gap={'16px'}
              alignItems={isDesktop ? 'flex-end' : 'flex-start'}
            >
              <Typography variant={'body1'} tabIndex={0}>
                {disabledVeteran.firstName} {disabledVeteran.lastName}
              </Typography>
              {isDesktop && (
                <Divider
                  orientation={'vertical'}
                  flexItem
                  aria-hidden={'true'}
                  sx={{ borderColor: 'primary.shade_100' }}
                />
              )}
              <Typography variant={'body7'} tabIndex={0}>
                {disabledVeteran.engFirstName} {disabledVeteran.engLastName}
              </Typography>
            </Stack>
          </Stack>
          <Divider
            orientation={'horizontal'}
            flexItem
            aria-hidden={'true'}
            sx={{ borderColor: 'primary.shade_100' }}
          />
          <Stack gap={'8px'}>
            <Stack direction={'row'} gap={'8px'} alignItems={'flex-end'}>
              <Typography variant={'body7'} tabIndex={0}>
                {dictionary.he.id}
              </Typography>
              <Divider
                orientation={'vertical'}
                flexItem
                aria-hidden={'true'}
                sx={{ borderColor: 'primary.shade_100' }}
              />
              <Typography variant={'body7'} tabIndex={0}>
                {dictionary.en.id}
              </Typography>
            </Stack>
            <Stack direction={'row'} gap={'16px'} alignItems={'flex-end'}>
              <Typography variant={'body1'} tabIndex={0}>
                {disabledVeteran.israeliId}
              </Typography>
            </Stack>
          </Stack>
          <Divider
            orientation={'horizontal'}
            flexItem
            aria-hidden={'true'}
            sx={{ borderColor: 'primary.shade_100' }}
          />
          <Stack gap={'8px'}>
            <Stack direction={'row'} gap={'8px'} alignItems={'flex-end'}>
              <Typography variant={'body7'} tabIndex={0}>
                {dictionary.he.caseNumber}
              </Typography>
              <Divider
                orientation={'vertical'}
                flexItem
                aria-hidden={'true'}
                sx={{ borderColor: 'primary.shade_100' }}
              />
              <Typography variant={'body7'} tabIndex={0}>
                {dictionary.en.caseNumber}
              </Typography>
            </Stack>
            <Stack direction={'row'} gap={'16px'} alignItems={'flex-end'}>
              <Typography variant={'body1'} tabIndex={0}>
                {disabledVeteran.caseNumber}
              </Typography>
            </Stack>
          </Stack>
          {disabledVeteran.disabilityValidTo && (
            <>
              <Divider
                orientation={'horizontal'}
                flexItem
                aria-hidden={'true'}
                sx={{ borderColor: 'primary.shade_100' }}
              />

              <Stack gap={'8px'}>
                <Stack direction={'row'} gap={'8px'} alignItems={'flex-end'}>
                  <Typography variant={'body7'} tabIndex={0}>
                    {dictionary.he.expiration}
                  </Typography>
                  <Divider
                    orientation={'vertical'}
                    flexItem
                    aria-hidden={'true'}
                    sx={{ borderColor: 'primary.shade_100' }}
                  />
                  <Typography variant={'body7'} tabIndex={0}>
                    {dictionary.en.expiration}
                  </Typography>
                </Stack>
                <Stack direction={'row'} gap={'16px'} alignItems={'flex-end'}>
                  <Typography variant={'body1'} tabIndex={0}>
                    {disabledVeteran.disabilityValidTo}
                  </Typography>
                  <Divider
                    orientation={'vertical'}
                    flexItem
                    aria-hidden={'true'}
                    sx={{ borderColor: 'primary.shade_100' }}
                  />
                  <Typography variant={'body7'} tabIndex={0}>
                    {dictionary.permanentEn}
                  </Typography>
                </Stack>
              </Stack>
            </>
          )}
        </Stack>
        <Box sx={{ p: 0, m: 0 }}>
          {(disabledVeteran?.queueExemptionRemark ||
            disabledVeteran?.serviceDogRemark) && (
            <Badge
              sx={{
                maxWidth: '100%',
                bgcolor: 'purple2.shade_100',
                border: '1px solid',
                borderColor: 'purple2.shade_200',
                padding: '8px 16px',
                borderRadius: '8px',
                display: 'flex',
                flexFlow: 'wrap',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {disabledVeteran?.queueExemptionRemark && (
                <>
                  <Avatar
                    alt=''
                    src={PurpleCheckMark}
                    sx={{ width: '16px', height: '16px' }}
                  />
                  <Typography variant={'body7'}>
                    {disabledVeteran?.queueExemptionRemark}
                  </Typography>
                  <Typography
                    variant='body6'
                    sx={{ width: '100%', textAlign: 'right' }}
                  >
                    {disabledVeteran?.queueExemptionRemarkEnglish}
                  </Typography>
                </>
              )}

              {disabledVeteran?.serviceDogRemark && (
                <>
                  {disabledVeteran?.queueExemptionRemark && (
                    <Divider
                      sx={{ borderColor: '#E4C1FF', width: '100%', m: '5px 0' }}
                      aria-hidden={true}
                    />
                  )}
                  <Avatar
                    alt=''
                    src={PurpleCheckMark}
                    sx={{ width: '16px', height: '16px' }}
                  />
                  <Typography variant={'body7'}>
                    {disabledVeteran?.serviceDogRemark}
                  </Typography>
                  <Typography
                    variant='body6'
                    sx={{ width: '100%', textAlign: 'right' }}
                  >
                    {disabledVeteran?.serviceDogRemarkEnglish}
                  </Typography>
                </>
              )}
            </Badge>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};
